import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { CONSUMER_APP_URL, CREDIT_PORTAL_URL, isProduction, PATH, useQuery, P, } from 'shared';
import { pushEdsSDKPagesVisited } from '../slice';
import { useEDSPreventBacktracking } from './_hooks';
import { useApplicationEDS } from './_hooks/useApplicationEDS';
import { useEDSStore } from './_store/eds.store';
import { getIntroSlug } from './_utils/getSlugs';
import { gotoAddInfo, gotoDecline, gotoError, gotoResult, } from './_utils/gotoURL';
import DisabledEDS from './DisabledEDS';
function ApplicationEDS() {
    // Store
    const { edsEnabled, edsApplicant, edsInfoForm, edsResult } = useEDSStore();
    // Has Credit Application
    const creditApplicationId = edsApplicant?.creditApplication?.applicationId;
    const storeNumber = edsApplicant?.location?.adsStoreNumber;
    const { edsCFEPagesVisited } = useEDSPreventBacktracking({
        currentPage: 'ApplicationEDS',
    });
    // Vertical Type
    const verticalCheck1 = edsApplicant?.location?.vertical === 'ophthalmology_optometry' ||
        edsApplicant?.location?.vertical === 'dentistry'
        ? true
        : false;
    const verticalCheck2 = edsInfoForm?.specialty === 'ophthalmology_optometry' ||
        edsInfoForm?.specialty === 'dentistry'
        ? true
        : false;
    // Placement ID
    const PLACEMENT_ID = verticalCheck1 || verticalCheck2
        ? 'iframe-overlay-alphaeon-vision'
        : 'iframe-overlay-alphaeon';
    const PAGES_THAT_PREVENT_BACKTRACKING = [
        'unified/offer-application/eligibility',
        'unified/prequal-offer',
    ];
    // State
    const [hasInit, setHasInit] = useState(false);
    const [currentPage, setCurrentPage] = useState(undefined);
    const pageOkayed = useRef(null);
    const creditApplicationIdRef = useRef(creditApplicationId);
    const canWaterfallRef = useRef(false);
    const currentAppIdRef = useRef(undefined);
    // Update refs when state changes
    useEffect(() => {
        creditApplicationIdRef.current = creditApplicationId;
    }, [creditApplicationId]);
    // Hooks
    const { queryObject: query } = useQuery();
    const history = useHistory();
    const dispatch = useDispatch();
    const edsSDKPagesVisited = useSelector((state) => state.root.creditPortal.eds.edsSDKPagesVisited);
    const edsLocationId = useSelector((state) => state.root.creditPortal.eds.edsLocationId);
    // API
    const { lenderRequirements, postVerifyEDS, postEventsEDS } = useApplicationEDS({
        applicationId: currentAppIdRef.current,
    });
    // Query Params
    const locationType = query.get('locationType');
    const placementId = query.get('placementId');
    // Track Card Event
    const trackCardEvent = async (eventName, cardResult) => {
        // Post Event API Call
        if ('pageName' in cardResult) {
            const pageName = cardResult.pageName;
            setCurrentPage(pageName);
        }
        const result = await postEventsEDS({
            applicationId: creditApplicationIdRef.current ?? '',
            postConsumerApplicationsV2CreditApplicationsApplicationIdEdsEvents: {
                name: eventName,
                body: {
                    ...cardResult,
                },
            },
        });
        // Access the errorClass and errorMessage from the response
        if ('data' in result && result.data) {
            const { errorClass, errorMessage } = result.data;
            if (errorClass || errorMessage) {
                handleEventError({
                    errorClass: errorClass,
                });
            }
        }
        // Capture offer received event
        if (eventName === 'CARD:UNIFIED_OFFERS_RECEIVED') {
            handleOffersReceived({
                result: cardResult?.result,
                prequalId: cardResult?.prequalificationId,
                creditAppId: creditApplicationIdRef.current,
            });
        }
        if (eventName === 'CARD:RECEIVE_ACCOUNT_EXISTS') {
            // If account exists, handle account exists logic
            handleAccountExists({
                creditAppId: creditApplicationIdRef.current,
            });
        }
        // Push to additional info page
        if (eventName === 'CARD:APPLICATION_COMPLETED' && canWaterfallRef.current) {
            handleApplicationCompleted({
                creditAppId: creditApplicationIdRef.current,
            });
        }
        // Handle modal close - redirect to consumer frontend
        if (eventName === 'CARD:CLOSE_OVERLAY' &&
            sessionStorage.getItem('edsApplicationCompleted') !== 'true') {
            handleCloseOverlay({
                canWaterfall: canWaterfallRef.current,
                currentAppId: currentAppIdRef.current,
            });
        }
    };
    const handleOffersReceived = ({ result, prequalId, creditAppId, }) => {
        // Mark Can Waterfall
        if (result === 'NOT_QUALIFIED') {
            canWaterfallRef.current = true;
        }
        // Make Post Event API Call
        if (creditAppId) {
            const prequalStatus = result.toLocaleLowerCase();
            postVerifyEDS({
                applicationId: creditAppId,
                postConsumerApplicationsV2CreditApplicationsApplicationIdEdsVerify: {
                    prequalificationId: prequalId,
                    applicationStatus: prequalStatus === 'pending_approval' ? 'pending' : prequalStatus,
                },
            });
        }
    };
    const handleApplicationCompleted = ({ creditAppId, }) => {
        // Check Lender Reqs
        if (creditAppId) {
            //set some session storage variable to not close the overlay when waiting for react state to update
            sessionStorage.setItem('edsApplicationCompleted', 'true');
            /**
             * Trigger Lender Requirements API call
             * check useEffect for lenderRequirements
             */
            currentAppIdRef.current = creditAppId;
        }
        else {
            gotoDecline(history);
        }
    };
    const handleAccountExists = ({ creditAppId, }) => {
        useEDSStore.setState({
            edsResult: {
                isExists: true,
                creditAppId: creditAppId,
            },
        });
        gotoResult(history);
    };
    const handleCloseOverlay = ({ canWaterfall, currentAppId, }) => {
        const hasNoResult = !edsResult ||
            (!edsResult.isExists && !edsResult.isDenied && !edsResult.isFailed);
        if (hasNoResult && CONSUMER_APP_URL && (!currentAppId || !canWaterfall)) {
            history.push(CONSUMER_APP_URL);
        }
    };
    const handleEventError = ({ errorClass, }) => {
        const fatalErrors = [
            'Helpers::Exceptions::ThresholdError::EDSALUCCICreationFailure',
            'Helpers::Exceptions::ThresholdError::EDSALUNoValidAccountsFound',
        ];
        if (errorClass && fatalErrors.includes(errorClass)) {
            killBreadPaymentsOverlay();
            gotoError(history);
            return;
        }
    };
    const killBreadPaymentsOverlay = () => {
        const breadPaymentsElement = document.getElementById('epjs-overlay-div');
        if (breadPaymentsElement) {
            breadPaymentsElement.remove();
        }
    };
    useEffect(() => {
        if (lenderRequirements &&
            lenderRequirements.hasOwnProperty('collectAdditionalInfoSubprime')) {
            handleCloseOverlay({
                canWaterfall: canWaterfallRef.current,
                currentAppId: currentAppIdRef.current,
            });
            sessionStorage.removeItem('edsApplicationCompleted');
            const isSubprime = lenderRequirements.collectAdditionalInfoSubprime;
            if (isSubprime === 'true' || isSubprime === true) {
                gotoAddInfo(history);
            }
            if (isSubprime === 'false' || isSubprime === false) {
                gotoDecline(history);
            }
        }
    }, [lenderRequirements]);
    // Effects
    useEffect(() => {
        if (window.BreadPayments && creditApplicationId) {
            const productPlacement = {
                financingType: 'card',
                locationType: locationType ?? 'landing',
                placementId: placementId ?? PLACEMENT_ID,
                domID: 'bread-payments-landing-placement',
            };
            const setupConfig = {
                loyaltyID: '',
                storeNumber: storeNumber || 99996,
                existingCardHolder: false,
                env: isProduction() ? 'PROD' : 'STAGE',
            };
            if (hasInit === false && edsCFEPagesVisited.length === 0) {
                // checking edsPagesVisited prevents the SDK from opening when you click back and land on this page.
                sessionStorage.removeItem('edsApplicationCompleted'); // reset application completed status on new init
                // Setup and Register
                window.BreadPayments.setup(setupConfig);
                window.BreadPayments.openExperienceForPlacement(productPlacement);
                // Mark init
                setHasInit(true);
            }
        }
    }, [window.BreadPayments, canWaterfallRef.current, hasInit]);
    useEffect(() => {
        if (hasInit === true) {
            // Events
            window.BreadPayments.on('CARD:*', function (cardResult) {
                const eventName = this.event;
                trackCardEvent(eventName, cardResult);
            });
        }
    }, [window.BreadPayments, hasInit]);
    useEffect(() => {
        if (currentPage) {
            const isNewPage = !edsSDKPagesVisited.includes(currentPage);
            if (!isNewPage && pageOkayed.current !== currentPage) {
                if (PAGES_THAT_PREVENT_BACKTRACKING.includes(currentPage)) {
                    killBreadPaymentsOverlay();
                    history.push(edsLocationId
                        ? `${CREDIT_PORTAL_URL + PATH.EDS.BASE + PATH.EDS.INDEX}?locationId=${edsLocationId}`
                        : CREDIT_PORTAL_URL);
                }
            }
            else if (isNewPage) {
                dispatch(pushEdsSDKPagesVisited(currentPage));
                pageOkayed.current = currentPage;
            }
        }
    }, [currentPage, edsSDKPagesVisited]);
    return (_jsxs("div", { className: "container min-height-600", children: [edsEnabled && (_jsx("div", { id: "bread-payments-landing-placement", className: "text-center" })), (edsEnabled === null || !creditApplicationId) && (_jsx("div", { className: "max-width-600 mar-auto text-center", children: _jsxs(P, { children: ["Please re-start the application", ' ', _jsx(Link, { to: getIntroSlug(), children: "here" }), "."] }) })), edsEnabled === false && _jsx(DisabledEDS, {})] }));
}
export default ApplicationEDS;
